/**
 * Location API Contract
 *
 * Mirrors: backend/src/routes/location.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateLocationInputSchema, UpdateLocationInputSchema, AddLocationManagerInputSchema, LocationResponseSchema, } from "@hourly/api/location";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Contracts
// ============================================
/**
 * GET /api/locations
 * List all locations for the authenticated user's company
 * CompanyId is extracted from JWT token
 */
export const getLocations = oc
    .route({
    method: "GET",
    path: "/locations",
    summary: "List locations for a company",
    tags: ["Locations"],
})
    .input(z.object({
    includeDeactivated: z
        .union([z.literal("true"), z.literal("false"), z.boolean()])
        .optional()
        .default(false)
        .transform((val) => val === "true" || val === true),
}))
    .output(z.array(LocationResponseSchema));
/**
 * POST /api/locations
 * Create a new location for the authenticated user's company
 * CompanyId is extracted from JWT token
 * Returns 201 Created on success
 */
export const createLocation = oc
    .route({
    method: "POST",
    path: "/locations",
    summary: "Create a new location",
    tags: ["Locations"],
    successStatus: 201,
})
    .input(CreateLocationInputSchema.omit({ companyId: true }))
    .output(LocationResponseSchema);
/**
 * GET /api/locations/:id
 * Get a location by ID
 */
export const getLocation = oc
    .route({
    method: "GET",
    path: "/locations/{id}",
    summary: "Get location by ID",
    tags: ["Locations"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationResponseSchema);
/**
 * PATCH /api/locations/:id
 * Update a location
 */
export const updateLocation = oc
    .route({
    method: "PATCH",
    path: "/locations/{id}",
    summary: "Update a location",
    tags: ["Locations"],
})
    .input(z.object({
    id: uuidSchema,
}).merge(UpdateLocationInputSchema))
    .output(LocationResponseSchema);
/**
 * DELETE /api/locations/:id
 * Delete a location
 * Returns 204 No Content on success
 */
export const deleteLocation = oc
    .route({
    method: "DELETE",
    path: "/locations/{id}",
    summary: "Delete a location",
    tags: ["Locations"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
/**
 * POST /api/locations/:id/managers
 * Add a manager to a location
 * Returns 201 Created on success
 */
export const addLocationManager = oc
    .route({
    method: "POST",
    path: "/locations/{id}/managers",
    summary: "Add a manager to a location",
    tags: ["Locations"],
    successStatus: 201,
})
    .input(z.object({
    id: uuidSchema,
}).merge(AddLocationManagerInputSchema))
    .output(LocationResponseSchema);
/**
 * DELETE /api/locations/:id/managers/:userId
 * Remove a manager from a location
 */
export const removeLocationManager = oc
    .route({
    method: "DELETE",
    path: "/locations/{id}/managers/{userId}",
    summary: "Remove a manager from a location",
    tags: ["Locations"],
})
    .input(z.object({
    id: uuidSchema,
    userId: uuidSchema,
}))
    .output(LocationResponseSchema);
/**
 * POST /api/locations/:id/deactivate
 * Deactivate a location
 */
export const deactivateLocation = oc
    .route({
    method: "POST",
    path: "/locations/{id}/deactivate",
    summary: "Deactivate a location",
    tags: ["Locations"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationResponseSchema);
/**
 * POST /api/locations/:id/reactivate
 * Reactivate a deactivated location
 */
export const reactivateLocation = oc
    .route({
    method: "POST",
    path: "/locations/{id}/reactivate",
    summary: "Reactivate a location",
    tags: ["Locations"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationResponseSchema);
// ============================================
// Contract Router
// ============================================
export const locationContract = {
    getLocations: getLocations,
    create: createLocation,
    get: getLocation,
    update: updateLocation,
    delete: deleteLocation,
    addManager: addLocationManager,
    removeManager: removeLocationManager,
    deactivate: deactivateLocation,
    reactivate: reactivateLocation,
};
//# sourceMappingURL=location.js.map